import { Checkbox, type TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import commaNumber from 'comma-number';
import { useMemo } from 'react';

import IncrementBadge from '@/components/Badge/IncrementBadge';
import {
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_GRAPH,
  CHART_X_FIELD_NAME,
} from '@/entities/chart/model/constants';
import { getChartMetricLabel } from '@/features/(charts)/chart-metric-policy';
import useChartLabel from '@/features/(charts)/chart-renderer/hooks/use-chart-label';
import chartUtils, {
  formatDisplayLabelsForTable,
} from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { ChartTopNumberMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import { getTimezoneDayjs } from '@/utils/time';
import { calIncrementAmount } from '@/utils/util';

type Props = {
  chartInfo: ChartContextProps;
};

interface DataType {
  [key: string]: StringNumberValue;
}

const useChartCandidateTable = (props: Props) => {
  const { state, setState, selectedField, setSelectedField } = props.chartInfo;
  const tableData = props.chartInfo.tableQuery.data;
  if (!tableData) throw new Error('tableData is required');

  const isTop =
    selectedField.method !== CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED;

  const getLabel = useChartLabel(tableData.field_item_meta);

  const singleSortedData: ChartSingleTableRes = chartUtils.getSortedRowItems(
    props.chartInfo.state,
    tableData,
    state.field.candidates || [],
    true,
  ) as ChartSingleTableRes;

  const crossSortedData: ChartCrossTableRes = chartUtils.getSortedRowItems(
    props.chartInfo.state,
    tableData,
    state.field.candidates || [],
    true,
  ) as ChartCrossTableRes;

  const dateLabels = chartUtils.getDateLabelsByUnit({
    dateRange: chartUtils.getTimeRangeByTerm(
      getTimezoneDayjs(),
      state.target.term,
    ),
    unit: state.target.term.unit,
  });

  const compareDateRange = chartUtils.getCompareTimeRangeByTerm(
    getTimezoneDayjs(),
    state.target.term,
    state.target.compare_term,
  );

  const compareDateLabels = chartUtils.getDateLabelsByUnit({
    dateRange: compareDateRange,
    unit: state.target.term.unit,
  });

  const columns: ColumnsType<any> = useMemo(() => {
    if (state.historical.value) {
      const frequency = state.historical.frequency;
      const formattedDateLabels = frequency
        ? formatDisplayLabelsForTable(dateLabels, frequency)
        : dateLabels;
      const formattedCompareDateLabels = frequency
        ? formatDisplayLabelsForTable(compareDateLabels, frequency)
        : compareDateLabels;

      if (state.target.is_compare) {
        const compareColumns = dateLabels.map((date, index) => {
          const compareDate = compareDateLabels[index];
          return {
            title: `${formattedCompareDateLabels[index]} / ${formattedDateLabels[index]}`,
            dataIndex: date,
            key: date,
            children: [
              {
                title: `Previous`,
                dataIndex: `${compareDate}-prev`,
                key: `${compareDate}-prev`,
                render: (text: number) => {
                  return commaNumber(text);
                },
              },
              {
                title: 'Current',
                dataIndex: `${date}-current`,
                key: `${date}-current`,
                render: (text: number) => {
                  return commaNumber(text);
                },
              },
              {
                title: '%Change',
                dataIndex: `${date}-change`,
                key: `${date}-change`,
                render: (text: number) => {
                  return <IncrementBadge amount={text} />;
                },
              },
            ],
          };
        });
        return [
          {
            title: 'Item',
            dataIndex: CHART_X_FIELD_NAME,
            key: CHART_X_FIELD_NAME,
            fixed: 'left',
            render: (text: string) => {
              return getLabel(state.field.type, text);
            },
          },
          ...compareColumns,
        ];
      }
      return [
        {
          title: 'Item',
          dataIndex: CHART_X_FIELD_NAME,
          key: CHART_X_FIELD_NAME,
          fixed: 'left',
          render: (text: string) => {
            return getLabel(state.field.type, text);
          },
        },
        ...dateLabels.map((date, index) => {
          return {
            title: formattedDateLabels[index],
            dataIndex: date,
            key: date,
            width: 80,
          };
        }),
      ];
    }

    if (state.target.is_compare) {
      // COMBO_CHART compare: 각 metric별로 Current/Previous/Change 컬럼 생성
      if (state.graph === CHART_GRAPH.COMBO_CHART) {
        const primaryMetricName =
          getChartMetricLabel(state.series[0]?.measure.metric) || 'Primary';
        const secondaryMetricName = state.series[1]?.measure
          ? getChartMetricLabel(state.series[1]?.measure.metric) || 'Secondary'
          : 'Secondary';

        return [
          {
            title: 'Item',
            dataIndex: CHART_X_FIELD_NAME,
            key: CHART_X_FIELD_NAME,
            fixed: 'left',
            render: (text: string) => {
              return getLabel(state.field.type, text);
            },
          },
          {
            title: primaryMetricName,
            key: 'primary_metrics',
            children: [
              {
                title: 'Previous',
                dataIndex: `${primaryMetricName}_prev`,
                key: `${primaryMetricName}_prev`,
                render: (text: number) => {
                  return commaNumber(text);
                },
              },
              {
                title: 'Current',
                dataIndex: `${primaryMetricName}_current`,
                key: `${primaryMetricName}_current`,
                render: (text: number) => {
                  return commaNumber(text);
                },
              },
              {
                title: '%Change',
                dataIndex: `${primaryMetricName}_change`,
                key: `${primaryMetricName}_change`,
                render: (text: number) => {
                  return <IncrementBadge amount={text} />;
                },
              },
            ],
          },
          {
            title: secondaryMetricName,
            key: 'secondary_metrics',
            children: [
              {
                title: 'Previous',
                dataIndex: `${secondaryMetricName}_prev`,
                key: `${secondaryMetricName}_prev`,
                render: (text: number) => {
                  return commaNumber(text);
                },
              },
              {
                title: 'Current',
                dataIndex: `${secondaryMetricName}_current`,
                key: `${secondaryMetricName}_current`,
                render: (text: number) => {
                  return commaNumber(text);
                },
              },
              {
                title: '%Change',
                dataIndex: `${secondaryMetricName}_change`,
                key: `${secondaryMetricName}_change`,
                render: (text: number) => {
                  return <IncrementBadge amount={text} />;
                },
              },
            ],
          },
        ];
      }

      return [
        {
          title: 'Item',
          dataIndex: CHART_X_FIELD_NAME,
          key: CHART_X_FIELD_NAME,
          fixed: 'left',
          render: (text: string) => {
            return getLabel(state.field.type, text);
          },
        },
        {
          title: 'Previous',
          dataIndex: 'prev',
          key: 'prev',
          render: (text: number) => {
            return commaNumber(text);
          },
        },
        {
          title: 'Current',
          dataIndex: 'current',
          key: 'current',
          render: (text: number) => {
            return commaNumber(text);
          },
        },
        {
          title: '%Change',
          dataIndex: 'change',
          key: 'change',
          render: (text: number) => {
            return <IncrementBadge amount={text} />;
          },
        },
      ];
    }

    // COMBO_CHART: 두 개의 metric 컬럼으로 분리
    if (state.graph === CHART_GRAPH.COMBO_CHART) {
      const primaryMetricName =
        getChartMetricLabel(state.series[0]?.measure.metric) || 'Primary';
      const secondaryMetricName = state.series[1]?.measure
        ? getChartMetricLabel(state.series[1]?.measure.metric) || 'Secondary'
        : 'Secondary';

      return [
        {
          title: 'Item',
          dataIndex: CHART_X_FIELD_NAME,
          key: CHART_X_FIELD_NAME,
          fixed: 'left',
          render: (text: string) => {
            return getLabel(state.field.type, text);
          },
        },
        {
          title: primaryMetricName,
          dataIndex: 'primary_value',
          key: 'primary_value',
          render: (text: number) => {
            return commaNumber(text);
          },
        },
        {
          title: secondaryMetricName,
          dataIndex: 'secondary_value',
          key: 'secondary_value',
          render: (text: number) => {
            return commaNumber(text);
          },
        },
      ];
    }

    // 기본 단일 Value 컬럼
    return [
      {
        title: 'Item',
        dataIndex: CHART_X_FIELD_NAME,
        key: CHART_X_FIELD_NAME,
        fixed: 'left',
        render: (text: string) => {
          return getLabel(state.field.type, text);
        },
      },
      {
        title: 'Value',
        dataIndex: 'value',
        key: 'value',
        render: (text: number) => {
          return commaNumber(text);
        },
      },
    ];
  }, [
    state.historical.value,
    state.historical.frequency,
    state.target.is_compare,
    state.graph,
    state.field.type,
    state.series,
    dateLabels,
    compareDateLabels,
    getLabel,
  ]);

  const dataSource = useMemo(() => {
    if (state.historical.frequency) {
      const formatter = chartUtils.getFormatByFrequency(
        state.historical.frequency,
      );

      if (state.target.is_compare) {
        // Periodic compare
        return crossSortedData.tables[0]?.map((item) => {
          const dateFields: Record<string, number | null> = {};

          const compareData = crossSortedData.compare_tables?.[0]?.find(
            (d) => d.x === item.x,
          );

          dateLabels.forEach((date, index) => {
            const compareDate = compareDateLabels[index];
            dateFields[`${date}-current`] = 0;
            dateFields[`${compareDate}-prev`] = 0;
            dateFields[`${date}-change`] = 0;
          });

          item?.values?.forEach((value) => {
            const formattedDate = formatter(
              getTimezoneDayjs(value.y.toString()),
            );
            dateFields[`${formattedDate}-current`] = value.value;
          });

          compareData?.values?.forEach((value) => {
            const formattedDate = formatter(
              getTimezoneDayjs(value.y.toString()),
            );
            dateFields[`${formattedDate}-prev`] = value.value;
          });

          dateLabels.forEach((date, index) => {
            const compareDate = compareDateLabels[index];
            const current = dateFields[`${date}-current`];
            const prev = dateFields[`${compareDate}-prev`];
            dateFields[`${date}-change`] = calIncrementAmount(
              current || 0,
              prev || 0,
            );
          });

          return {
            [CHART_X_FIELD_NAME]: item.x,
            ...dateFields,
          };
        });
      }

      // Periodic non compare
      return (crossSortedData.tables[0] || []).map((item) => {
        const fields = chartUtils.getKeyValueMapByKeys(dateLabels);

        item?.values?.forEach((value) => {
          const formattedDate = formatter(getTimezoneDayjs(value.y.toString()));
          fields[formattedDate] = value.value;
        });

        return {
          [CHART_X_FIELD_NAME]: item.x,
          ...fields,
        };
      });
    }

    // Total compare
    if (state.target.is_compare) {
      if (state.graph === CHART_GRAPH.COMBO_CHART) {
        // COMBO_CHART compare: 각 metric에 대해 current/prev/change 표시
        const primaryMetricName =
          getChartMetricLabel(state.series[0]?.measure.metric) || 'Primary';
        const secondaryMetricName = state.series[1]
          ? getChartMetricLabel(state.series[1].measure.metric) || 'Secondary'
          : 'Secondary';

        return (singleSortedData.tables[0] || []).map((item) => {
          const currentData = item;
          const compareData = singleSortedData.compare_tables?.[0]?.find(
            (d) => d.x === item.x,
          );

          // Secondary data
          const currentSecondaryData = singleSortedData.tables[1]?.find(
            (d) => d.x === item.x,
          );
          const compareSecondaryData =
            singleSortedData.compare_tables?.[1]?.find((d) => d.x === item.x);

          return {
            [CHART_X_FIELD_NAME]: item.x,
            [`${primaryMetricName}_current`]: currentData?.value || 0,
            [`${primaryMetricName}_prev`]: compareData?.value || 0,
            [`${primaryMetricName}_change`]: calIncrementAmount(
              currentData?.value || 0,
              compareData?.value || 0,
            ),
            [`${secondaryMetricName}_current`]:
              currentSecondaryData?.value || 0,
            [`${secondaryMetricName}_prev`]: compareSecondaryData?.value || 0,
            [`${secondaryMetricName}_change`]: calIncrementAmount(
              currentSecondaryData?.value || 0,
              compareSecondaryData?.value || 0,
            ),
          };
        });
      }

      return (singleSortedData.tables[0] || []).map((item) => {
        const currentData = item;
        const compareData = singleSortedData.compare_tables?.[0]?.find(
          (d) => d.x === item.x,
        );
        return {
          [CHART_X_FIELD_NAME]: item.x,
          current: currentData?.value || 0,
          prev: compareData?.value || 0,
          change: calIncrementAmount(
            currentData?.value || 0,
            compareData?.value || 0,
          ),
        };
      });
    }

    // Total non compare
    if (state.graph === CHART_GRAPH.COMBO_CHART) {
      return singleSortedData.tables[0]?.map((item) => {
        // COMBO_CHART의 경우 tables[1]에서 corresponding 데이터를 찾기
        const secondaryItem = singleSortedData.tables[1]?.find(
          (secItem) => secItem.x === item.x,
        );

        return {
          [CHART_X_FIELD_NAME]: item.x,
          primary_value: item.value || 0,
          secondary_value: secondaryItem?.value || 0,
        };
      });
    }

    return singleSortedData.tables[0]?.map((item) => {
      return {
        [CHART_X_FIELD_NAME]: item.x,
        value: item.value,
      };
    });
  }, [
    state.historical.frequency,
    state.target.is_compare,
    state.graph,
    state.series,
    singleSortedData.tables,
    singleSortedData.compare_tables,
    crossSortedData.tables,
    crossSortedData.compare_tables,
    dateLabels,
    compareDateLabels,
  ]);

  const renderedRowKeys = useMemo(() => {
    return (dataSource || []).map((item) => item[CHART_X_FIELD_NAME]);
  }, [dataSource]);

  const selectedRowKeys = useMemo(() => {
    if (state.field.entire) {
      if ((selectedField.items?.length || 0) > 0) {
        return selectedField.items;
      }
      return renderedRowKeys;
    }

    const topNumber = ChartTopNumberMap.get(selectedField.method);
    if (
      selectedField.method === CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED
    ) {
      return selectedField.items || [];
    } else {
      if (state.historical.value) {
        return crossSortedData?.tables[0]?.slice(0, topNumber).map((d) => d.x);
      } else {
        return singleSortedData.tables[0]?.slice(0, topNumber).map((d) => d.x);
      }
    }
  }, [
    state.field.entire,
    state.historical.value,
    selectedField.method,
    selectedField.items,
    renderedRowKeys,
    crossSortedData?.tables,
    singleSortedData.tables,
  ]);

  const isAllSelected = useMemo(() => {
    const selectedRenderedRowCount = renderedRowKeys.filter((key) =>
      selectedRowKeys?.includes(key),
    ).length;

    return {
      checked:
        renderedRowKeys.length > 0 &&
        selectedRenderedRowCount === renderedRowKeys.length,
      halfCheck:
        selectedRenderedRowCount > 0 &&
        selectedRenderedRowCount < renderedRowKeys.length,
    };
  }, [renderedRowKeys, selectedRowKeys]);

  const onSelectAllClick = () => {
    const arr = renderedRowKeys;
    const isEveryRenderedRowSelected =
      renderedRowKeys.length > 0 &&
      renderedRowKeys.every((key) => selectedRowKeys?.includes(key));

    if (state.field.entire) {
      setState({
        ...state,
        selected_field: {
          method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
          items: [],
        },
      });
      setSelectedField({
        method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
        items: [],
      });
      return;
    }

    if (isTop) {
      if (isEveryRenderedRowSelected) {
        setState({
          ...state,
          field: {
            ...state.field,
            entire: false,
            candidates: [],
          },
          selected_field: {
            method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
            items: [],
          },
        });
        setSelectedField({
          method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
          items: [],
        });
      } else {
        setState({
          ...state,
          field: {
            ...state.field,
            entire: false,
            candidates: arr,
          },
          selected_field: {
            method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
            items: arr,
          },
        });
        setSelectedField({
          method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
          items: arr,
        });
      }
    } else {
      if (isEveryRenderedRowSelected) {
        setState({
          ...state,
          field: {
            ...state.field,
            entire: false,
            candidates: [],
          },
          selected_field: {
            method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
            items: [],
          },
        });
        setSelectedField({
          method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
          items: [],
        });
      } else {
        setState({
          ...state,
          field: {
            ...state.field,
            entire: false,
            candidates: arr,
          },
          selected_field: {
            method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
            items: arr,
          },
        });
        setSelectedField({
          method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
          items: arr,
        });
      }
    }
  };

  const onSelectItemClick = (item: StringNumberValue) => {
    if (state.field.entire) {
      const currentItems = selectedField.items || [];
      const currentSelectedRowKeys =
        currentItems.length > 0 ? currentItems : renderedRowKeys;
      const nextItems = [...currentSelectedRowKeys];
      nextItems.toggle(item);
      const normalizedNextItems =
        nextItems.length === renderedRowKeys.length ? [] : nextItems;
      const nextMethod =
        normalizedNextItems.length === 0
          ? CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5
          : CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED;
      setState({
        ...state,
        field: {
          ...state.field,
          entire: normalizedNextItems.length === 0,
          candidates: normalizedNextItems,
        },
        selected_field: {
          method: nextMethod,
          items: normalizedNextItems,
        },
      });
      setSelectedField({
        method: nextMethod,
        items: normalizedNextItems,
      });
      return;
    }

    const topNumber = ChartTopNumberMap.get(state.selected_field.method);
    if (isTop) {
      const arr = state.historical.value
        ? crossSortedData.tables[0]?.slice(0, topNumber).map((d) => d.x)
        : singleSortedData.tables[0]?.slice(0, topNumber).map((d) => d.x);

      if (
        selectedField.method === CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED
      ) {
        setSelectedField({
          method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
          items: arr,
        });
      } else {
        const newArr = [...arr];
        newArr.toggle(item);
        setState((prev) => {
          return {
            ...prev,
            field: {
              ...prev.field,
              entire: false,
              candidates: newArr,
            },
            selected_field: {
              ...prev.selected_field,
              method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
              items: newArr,
            },
          };
        });
        setSelectedField({
          method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
          items: newArr,
        });
      }
    } else {
      const newArr = [...(selectedField?.items || [])];
      newArr.toggle(item);
      setState((prev) => {
        return {
          ...prev,
          field: {
            ...prev.field,
            entire: false,
            candidates: newArr,
          },
          selected_field: {
            ...prev.selected_field,
            method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
            items: newArr,
          },
        };
      });
      setSelectedField({
        method: CHART_FIELD_ITEM_SELECTION_METHOD.USER_DEFINED,
        items: newArr,
      });
    }
  };

  const headerCheckbox = (
    <Checkbox
      checked={isAllSelected.checked}
      indeterminate={isAllSelected.halfCheck}
      onChange={onSelectAllClick}
    />
  );

  const rowSelection: TableProps<DataType>['rowSelection'] = {
    selectedRowKeys,
    type: 'checkbox',
    fixed: true,
    onSelect: (record: DataType) => {
      onSelectItemClick(record[CHART_X_FIELD_NAME]);
    },
    columnTitle: headerCheckbox,
  };

  return {
    columns,
    dataSource,
    rowSelection,
  };
};

export default useChartCandidateTable;
